<template>
  <div class="hotel_calendar--container" :style="{'--months-to-show': calendarMonthsToShow}"
       :class="{'hotel_calendar--container-vertical': isDisplayVertical}">

    <div v-if="calendarInitialized" class="calendar--container" :class="calendarContainerClasses">
      <a class="calendar--nav calendar--nav-previous" :class="calendarPrevClasses" href="#"
         @click.prevent="seekMonth(-1)" title="Navigate to previous month">
        <svg>
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#ic_chevron_left_24px"></use>
        </svg>
      </a>
      <a class="calendar--nav calendar--nav-next" :class="calendarNextClasses" href="#"
         @click.prevent="seekMonth(1)" title="Navigate to next month">
        <svg>
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#ic_chevron_right_24px"></use>
        </svg>
      </a>
      <div class="calendar--grid">
        <hotel-calendar-month v-for="(month, monthIndex) in calendarMonths" :key="month.year + '-' + month.month"
                              :month="month"
                              :ref="'month-' + month.year + '-' + month.month"
                              :class="getMonthClasses(monthIndex, calendarCurrentMonthIndex)"
                              @close="$emit('close')" @seek="seekMonth"></hotel-calendar-month>
      </div>

      <hotel-calendar-footer @close="$emit('close')"></hotel-calendar-footer>
    </div>

    <loading-spinner v-if="!calendarInitialized" class="hotel_calendar--rate_loader"></loading-spinner>

  </div>
</template>

<script lang="ts">
/*!
 * Copyright (C) 2022-2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2022-08-17
 *			- Refactor calendar.vue to hotelCalendar.vue.
 *
 *		2024-06-10 (jhoward)
 *			- Implement Typescript in <hotel-Calendar>.
 *			- Build HTML in <hotel-Calendar> so only JSON data needs to be pulled from the backend.
 *
 *		2024-07-09 (jhoward)
 *			- Throttle window.resize callbacks in <hotel-Calendar>.
 *			- Fix nav arrow placement in <hotel-Calendar>.
 *			- Fix the number of months being shown at once in <hotel-Calendar>.
 */

//@ts-ignore
import Vue from "vue/dist/vue.esm";
import {defineComponent} from "vue";
import HotelCalendarDay from "./hotelCalendarDay.vue";
import HotelCalendarMonth from "./hotelCalendarMonth.vue";
import HotelCalendarFooter from "./hotelCalendarFooter.vue";
import LoadingSpinner from "../LoadingSpinner.vue";
import { CalendarData, CalendarDay, CalendarMonth } from "./defs";

interface CalendarDataServerResponse {
  STATUS: 'SUCCESS' | 'ERROR';
  DATA: {
    CALENDAR: CalendarData;
    'CALENDAR-CALLBACK-UPDATE': string;
    'CALENDAR-WIDGET-CLASSES': string[];
  };
}

export default defineComponent({
  name: "hotel-calendar",
  components: {LoadingSpinner, HotelCalendarFooter, HotelCalendarMonth, HotelCalendarDay},
  props: {
    showSpecialOffers: {
      type: Boolean,
      default: false
    },
    loadCalendarCallback: {
      type: String,
      default: 'RATE-CALENDAR-RELOAD-PROMPT'
    },
    changeDatesCallback: {
      type: Function,
      required: true
    },
    pageId: {
      type: Number,
      default: 0
    },
    disallowSoldOutDates: {
      type: Boolean,
      default: false
    },
    isDisplayVertical: {
      type: Boolean,
      default: false
    },
    isBlackoutCalendar: {
      type: Boolean,
      default: false
    },
    offerCode: {
      type: String,
      default: ''
    },
    defaultCheckInDate: {
      type: String,
      default: ''
    },
    defaultCheckOutDate: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      calendarMonthsToShow: 12,
      calendarCurrentMonthIndex: 0,
      calendarInitialized: false,
      calendarLoaded: false,
      updateCallback: '',
      widgetClasses: [] as string[],

      initialCheckInDate: null as string | null,
      initialCheckOutDate: null as string | null,

      blankDay: {
        PRICE: 0,
        BASE_PRICE: 0,
        // HIGHLIGHT: false,
        // CHECK_IN: false,
        // CHECK_OUT: false,
        DISPLAY_RATES: false,
        OFFER_CODE: '',
        OFFER_CODE_STRIKE: '',
        ROOM_SEQUENCE_STRIKE: '',
        MIN_NIGHTS: 0,
        TITLE: '',
        BLANK: true,
        AVAILABLE: false,
        KEY: '',
      } as CalendarDay,

      currentUiBreakpoint: 'mobile',
      uiBreakpointTimer: -1,

      today: new Date((new Date()).toDateString())
    };
  },
  mounted() {
    window.addEventListener('resize', () => {
      window.clearTimeout(this.uiBreakpointTimer);
      this.uiBreakpointTimer = window.setTimeout(this.setCurrentUiBreakpoint, 128);
    }, {
      passive: true
    });
    requestAnimationFrame(this.setCurrentUiBreakpoint);

    this.loadTemplate();
  },
  watch: {
    calendarInitialized() {
      if (this.calendarInitialized) {
        window.setTimeout(() => {
          (this.$el.querySelector('.day--container-check_in') as HTMLElement | null)?.focus();
        }, 256);
      }
    },
    selectedDates() {
      //first check if any unavailable dates have been selected
      if (this.disallowSoldOutDates && this.$store.state.checkInDate && this.$store.state.checkOutDate) {
        let badDateFound = false;
        for (let i = 0, l = this.selectedDates.length - 1; i < l; i++) {
          const [year, month, day] = this.selectedDates[i].split('-');
          const yearMonth = year + '-' + month;
          if (this.$store.state.calendarData[yearMonth]?.[day] && this.isDayNoData(this.$store.state.calendarData[yearMonth][day])) {
            badDateFound = true;
            break;
          }
        }
        if (badDateFound) {
          window.showOverlayDialog({
            alertMode: true,
            confirmAccept: 'OK',
            confirmTitle: 'Invalid Date Selection',
            confirmBody: '<p>One or more unavailable dates selected.</p><p>Your check in/out dates have been reset.</p>',
            callback: 'RATE-CALENDAR-RELOAD'
          }, this.$el as HTMLElement);
          this.$store.dispatch('updateCheckInDate', this.initialCheckInDate);
          this.$store.dispatch('updateCheckOutDate', this.initialCheckOutDate);
          return;
        }
      }

      if (this.$store.state.checkInDate && this.$store.state.checkOutDate) {
        this.dateSelectCallback();
      }
    }
  },
  computed: {
    calendarMonths(): CalendarMonth[] {
      const months: CalendarMonth[] = [];
      for (let monthYear in this.$store.state.calendarData) {
        const monthData = this.$store.state.calendarData[monthYear];
        let monthWeeks: CalendarDay[][] = [];
        const titleParts = monthYear.split('-');
        const titleMonth = parseInt(titleParts[1], 10) - 1;
        const titleYear = parseInt(titleParts[0], 10);
        const firstDayOfWeek = (new Date(titleYear, titleMonth, 1)).getDay();
        const weeksInMonth = Math.ceil((Object.keys(monthData).length + firstDayOfWeek) / 7);
        for (let i = 0; i < weeksInMonth; i++) {
          let monthWeek = [];
          if (i === 0) {
            //first week
            for (let ii = 0; ii < firstDayOfWeek; ii++) {
              monthWeek.push({...this.blankDay, ...{KEY: monthYear + '-' + (0 - ii)}});
            }
            for (let ii = 0, ll = 7 - firstDayOfWeek; ii < ll; ii++) {
              const dayNumber = (ii + 1);
              const dayKey = '0' + dayNumber;
              const date = this.getDayDate(monthData[dayKey]);
              monthWeek.push({
                ...monthData['0' + (ii + 1)],
                ...{
                  TITLE: dayNumber,
                  DAY: dayNumber,
                  DATE: date,
                  BLANK: monthData[dayKey].BLANK,
                  BEFORE_TODAY: this.isDateBeforeToday(date)
                }
              });
            }
          } else if (i + 1 === weeksInMonth) {
            //last week
            for (let ii = 0; ii < 7; ii++) {
              const dayNumber = i * 7 + ii - firstDayOfWeek + 1;
              const dayKey = (dayNumber < 10) ? '0' + dayNumber : '' + dayNumber;
              if (monthData[dayKey]) {
                const date = this.getDayDate(monthData[dayKey]);
                monthWeek.push({
                  ...monthData[dayKey],
                  ...{
                    TITLE: dayNumber,
                    DAY: dayNumber,
                    DATE: date,
                    BLANK: monthData[dayKey].BLANK,
                    BEFORE_TODAY: this.isDateBeforeToday(date)
                  }
                });
              } else {
                monthWeek.push({...this.blankDay, ...{KEY: monthYear + '-' + dayNumber}});
              }
            }
          } else {
            //middle weeks
            for (let ii = 0; ii < 7; ii++) {
              const dayNumber = i * 7 + ii - firstDayOfWeek + 1;
              const dayKey = (dayNumber < 10) ? '0' + dayNumber : '' + dayNumber;
              const date = this.getDayDate(monthData[dayKey]);
              monthWeek.push({
                ...monthData[dayKey],
                ...{
                  TITLE: dayNumber,
                  DAY: dayNumber,
                  DATE: date,
                  BLANK: monthData[dayKey].BLANK,
                  BEFORE_TODAY: this.isDateBeforeToday(date)
                }
              });
            }
          }
          monthWeeks.push(monthWeek);
        }

        months.push({
          weeks: monthWeeks,
          title: window.pepDateFunctions.language.en.months[titleMonth] + ' ' + titleYear,
          year: titleYear,
          month: titleMonth
        });
      }
      return months;
    },

    calendarPrevClasses(): { [key: string]: boolean } {
      return {
        'calendar--nav-hidden': !this.calendarLoaded || this.calendarCurrentMonthIndex === 0 || this.isDisplayVertical
      };
    },
    calendarNextClasses(): { [key: string]: boolean } {
      return {
        'calendar--nav-hidden': !this.calendarLoaded || this.calendarCurrentMonthIndex >= this.calendarMonthsToShow - this.calendarNumberOfMonthsToShow || this.isDisplayVertical
      };
    },
    calendarNumberOfMonthsToShow() {
      if ('mobile' === this.currentUiBreakpoint) {
        return 1;
      }

      return 2;
    },

    calendarContainerClasses(): { [key: string]: boolean } {
      const classes: { [key: string]: boolean } = {
        'calendar--container-has_footer': true
      };

      this.widgetClasses.forEach(className => classes[className] = true);

      return classes;
    },

    checkInMonth(): number {
      return (new Date(this.$store.getters.bookingLinkStartDate))?.getMonth() || 0;
    },
    checkInYear(): number {
      return (new Date(this.$store.getters.bookingLinkStartDate))?.getFullYear() || 0;
    },
    selectedDates(): string[] {
      return this.$store.getters.selectedDates;
    }
  },
  methods: {
    setCurrentUiBreakpoint(): void {
      const htmlClassList = document.querySelector('html')?.classList;
      if (htmlClassList) {
        if (htmlClassList.contains('ui-mobile')) {
          this.currentUiBreakpoint = 'mobile';
        } else if (htmlClassList.contains('ui-tablet')) {
          this.currentUiBreakpoint = 'tablet';
        } else if (htmlClassList.contains('ui-desktop')) {
          this.currentUiBreakpoint = 'desktop';
        } else if (htmlClassList.contains('ui-widescreen')) {
          this.currentUiBreakpoint = 'widescreen';
        }
      }
    },

    isDateBeforeToday(date: Date | null): boolean {
      return !!date && date.getTime() < this.today.getTime();
    },

    getMonthClasses(monthIndex: number, currentMonthIndex: number): { [key: string]: boolean } {
      return {
        'calendar--month-hide': !this.isDisplayVertical && !(monthIndex === currentMonthIndex || monthIndex === currentMonthIndex + this.calendarNumberOfMonthsToShow - 1)
      };
    },


    getDayDate(day: CalendarDay): Date | null {
      const dateParts = day.KEY.split('-');
      const date = new Date(parseInt(dateParts[0], 10), parseInt(dateParts[1], 10) - 1, parseInt(dateParts[2], 10));

      return (!isNaN(date.getTime())) ? date : null;
    },
    scrollToCheckIn() {
      const checkInMonthNode = this.$refs['month-' + this.checkInYear + '-' + this.checkInMonth] as HTMLDivElement | null;
      const viewportNode = this.$refs.viewport as HTMLDivElement | null;

      if (checkInMonthNode && viewportNode) {
        viewportNode.scrollTop = checkInMonthNode.offsetTop || 0;
      }
    },
    initializeCalendar() {
      requestAnimationFrame(() => {
        this.initialCheckInDate = this.$store.state.checkInDate || this.$store.state.bookingLinkData?.start || this.defaultCheckInDate;
        this.initialCheckOutDate = this.$store.state.checkOutDate || this.$store.state.bookingLinkData?.end || this.defaultCheckOutDate;
        this.$store.dispatch('updateCheckInDate', this.initialCheckInDate);
        this.$store.dispatch('updateCheckOutDate', this.initialCheckOutDate);
        this.scrollToCheckIn();

        this.calendarInitialized = true;
      });
    },
    loadTemplate(forceReload = false) {
      console.log('loadTemplate', forceReload);
      if (!this.calendarLoaded || forceReload) {
        this.loadCalendar();
      }
    },
    async loadCalendar() {
      console.log('loadCalendar');
      const currentDate = new Date();
      const firstDate = new Date(currentDate.getFullYear(), currentDate.getMonth());
      let allMonths = [];

      for (let i = 0; i <= this.calendarMonthsToShow; i++) {
        const newDate = window.pepDateFunctions.addMonths(firstDate, i);
        allMonths.push(newDate.getFullYear() + '-' + (newDate.getMonth() + 1));
      }
      console.log('allMonths', allMonths);

      try {
        const response = await fetch('/library/api/resnet.php', {
          method: 'POST',
          cache: 'no-cache',
          headers: {
            // 'Content-type': 'application/x-www-form-urlencoded',
            'Content-type': 'application/json',
            'Accept': 'application/json'
          },
          redirect: 'follow',
          body: JSON.stringify({
            ACTION: 'GET_WIDGET_CALENDAR',
            MONTHS: allMonths,
            CHECK_IN_DATE: this.$store.state.bookingLinkData?.start || this.$store.state.checkInDate || this.defaultCheckInDate,
            CHECK_OUT_DATE: this.$store.state.bookingLinkData?.end || this.$store.state.checkOutDate || this.defaultCheckOutDate,
            PAGE_ID: this.$store.getters.bookingLinkRoomPageId || this.pageId,
            CALLBACK: this.loadCalendarCallback,
            USE_VUE: 'YES',
            SHOW_SPECIAL: this.showSpecialOffers ? 'YES' : 'NO',
            SHOW_BLACKOUT: this.isBlackoutCalendar ? 'YES' : 'NO',
            DATA_ONLY: 'YES',
            OFFER_CODE: this.offerCode,
          })
        });
        const json: CalendarDataServerResponse | null = await response.json();
        if (json?.STATUS === 'SUCCESS') {
          //dynamically update the template render and static render functions for the component
          this.$store.commit('updateState', ['calendarData', json.DATA.CALENDAR]);
          this.updateCallback = json.DATA['CALENDAR-CALLBACK-UPDATE'];
          this.widgetClasses = json.DATA['CALENDAR-WIDGET-CLASSES'];
          this.calendarLoaded = true;
          this.$emit('loaded');
          this.initializeCalendar();
        }
      } catch (e) {
        const error = e as Error;

        console.log('error', error);

        window.pepTracking.event('JS Error', 'Get Rate calendar', error.message, 0, true);

        $.ajax({
          url: '/library/api/capture-js-error.php',
          dataType: 'json',
          type: 'post',
          data: {
            je_message: 'Get Rate Calendar',
            je_source: 'hotelCalendar.vue',
            je_lineno: 0,
            je_colno: 0,
            je_url: window.location.href,
            je_stack: error.message
          }
        });
      }
    },

    dateSelectCallback() {
      if (this.$store.state.checkInDate && this.$store.state.checkOutDate && (this.$store.state.checkInDate !== this.initialCheckInDate || this.$store.state.checkOutDate !== this.initialCheckOutDate)) {
        this.changeDatesCallback(this.$store.state.checkInDate, this.$store.state.checkOutDate, this.initialCheckInDate, this.initialCheckOutDate, this.selectedDates);
      }
    },

    seekMonth(direction: number) {
      if (direction < 0 && this.calendarCurrentMonthIndex > 0) {
        this.calendarCurrentMonthIndex--;
      } else if (direction > 0 && this.calendarCurrentMonthIndex < this.calendarMonthsToShow - 1) {
        this.calendarCurrentMonthIndex++;
      }
    },


    isDayNoData(day: CalendarDay): boolean {
      //AVAILABLE is derived server side; it replaces the old (DISPLAY_RATES && 0 === PRICE) test
      //so that hiding rate information cannot change which days are selectable.
      return day.BLANK || day.BEFORE_TODAY || !day.AVAILABLE;
    },

    getDayObjectForDate(date: string | Date): CalendarDay | null {
      const dateParts = (date instanceof Date)
        ? [date.getFullYear(), date.getMonth() + 1, date.getDate()]
        : /^\d{4}-\d{2}-\d{2}$/.exec(date);
      if (dateParts) {
        const yearMonth = dateParts[0] + '-' + dateParts[1];
        return this.$store.state.calendarData[yearMonth]?.[dateParts[2]] || null;
      }

      return null;
    }
  }
})
</script>

<style scoped>
.hotel_calendar--container {
  --months-to-show: 12;
}

.calendar--wrapper-has_footer {
  padding-bottom: 3em;
}

.calendar--nav {
  height: 44px;
  left: 0;
  position: absolute;
  top: 0;
  width: 44px;
  z-index: 8;
  transition: opacity 132ms ease-in-out;
}

.calendar--nav-hidden {
  opacity: 0;
  visibility: hidden;
  transition: opacity 132ms ease-in-out, visibility 1ms linear 132ms;
}

.calendar--nav-next {
  left: auto;
  right: 0;
}

.calendar--nav svg {
  height: 100%;
  width: 100%;
}

html.ui-mobile .calendar--grid {
  grid-template-columns:	repeat(1, 100%);
}
.calendar--container-has_footer {
  padding-bottom: 2.4em;
}
</style>